/* Copyright (c) 2022 MOCA Systems, Inc. All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { IReportOptions } from '../models/ReportOptions';
import { ReportType } from '../types/ReportType';
import { ReportsFormManager } from './reports-form-manager.service';
import { ReportsUserPrefsService } from './reports-user-prefs.service';
import { reportsFormTypeConfig } from '../components/reportsFormTypeConfig';
import { ReportFormControl } from '../types/ReportFormControl';
import { Subject, Observable, of, BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { IAnyReportTypes } from 'src/app/services/fbdb-requests.interfaces';
import { SortBy } from '../types/SortBy';
import { GroupBy } from '../types/GroupBy';
import { ExportFormatType } from '../types/ExportFormatType';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { CustomFieldType } from 'src/app/app-data/state/CustomFieldsApi';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { dateCache } from '@touchplan/common';
import { ICustomFieldFilter } from '../../../app-data/models/TPFilterPredicates';
import { ReportPrefsFirebaseDatatypes } from '../types/ReportPrefs';
import { filterMapIterator } from 'src/app/utils/map-util';
import { SortGroupsBy } from '../types/SortGroupsBy';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { CloudStorageService, FirebaseStorageMetadata } from 'src/app/services/cloud-storage.service';
import { ICollapsedSwimlaneDropdownOption } from './reports-swimlane-data.service';
import { DEFAULT_REPORT_FORM_VALUES } from '../models/report-form.constants';

// unknown is deliberate, it prevents accessing anything meaningful (and meanfully) about the value in the map which means we
// can _reasonably_ safely use the "original" maps without needing to copy around id and create new structures
type keyOnlyMap = Map<string, unknown>;
type deletedAtMap = Map<string, IHasDeletedAt>;
interface IIdCache {
	plans?: keyOnlyMap;
	locations?: keyOnlyMap;
	roles?: keyOnlyMap;
	swimlanes?: keyOnlyMap;
	customFields?: keyOnlyMap;
	layoutCustomFields?: keyOnlyMap;
	archivedPlansDropDown?: keyOnlyMap;
}

interface IHasDeletedAt {
	view: { deletedAt?: string };
}

// set of controls that cannot be persisted as-is
const SPECIAL_CASE_CONTROLS_TO_PERSIST = new Map<ReportFormControl, any>([
	[ReportFormControl.ExportFormatTemplate, null],
]);

// set of controls that cannot be returned as-is
const SPECIAL_CASE_CONTROLS_TO_READ = new Map<ReportFormControl, any>([
	[ReportFormControl.ExportFormatTemplate, null],
]);

/**
 * Ties the various pieces of the Reports UI together: the ReportsComponent, the ReportsFormManager,
 * the ReportsUserPrefsPersistence, ...
 */
@Injectable()
export class ReportsService implements OnDestroy {
	private formGroup: UntypedFormGroup;

	private shutdownSubject$ = new Subject<void>();

	/** Cache of id data */
	private validIdsCache: IIdCache = {};

	private logger: ILoggerService;

	constructor(
		private formManager: ReportsFormManager,
		private userPrefsService: ReportsUserPrefsService,
		private featureFlagManager: FeatureFlagManager,
		private cloudStorageService: CloudStorageService
	) {
		this.logger = LoggerServiceFactory.create('ReportsService');
	}

	public isAdmin$ = new BehaviorSubject(false);

	/**
	 * Call as part of initialization with a set of observables to maintain a synchronous cache of the data.
	 * Should be called before anything that loads in preferences
	 */
	public startCachingIds(
		plan$: Observable<deletedAtMap>,
		location$: Observable<keyOnlyMap>,
		role$: Observable<keyOnlyMap>,
		customField$: Observable<Map<string, CustomFieldDefinition>>,
		swimlane$: Observable<keyOnlyMap>,
		layoutCustomFields$: Observable<Map<string, CustomFieldDefinition>>
	) {
		const checkForDeletedAt = (list: deletedAtMap) => {
			return new Map(
				filterMapIterator(list.entries(), ([__, val]) => {
					return !val?.view.deletedAt;
				})
			);
		};

		const filteredCustomField$ = customField$.pipe(
			map((cfList) => {
				return new Map(filterMapIterator(cfList.entries(), ([__, cf]) => cf.view.isEnabled));
			})
		);

		this.cacheIdFromObservable('plans', plan$.pipe(map((l) => checkForDeletedAt(l))));
		this.cacheIdFromObservable('locations', location$);
		this.cacheIdFromObservable('roles', role$);
		this.cacheIdFromObservable('swimlanes', swimlane$);
		this.cacheIdFromObservable('layoutCustomFields', layoutCustomFields$);
		this.cacheIdFromObservable('customFields', filteredCustomField$);
	}

	private cacheIdFromObservable(key: keyof IIdCache, ob$: Observable<keyOnlyMap>) {
		ob$.pipe(takeUntil(this.shutdownSubject$)).subscribe((list) => (this.validIdsCache[key] = list));
	}

	/**
	 * updates the form’s ExportFormatTemplate as the ExportFormatType field is tracked. If the type is not P6Excel, the template field is set to null
	 * Otherwise it checks cloud storage for a previously uploaded template and if found sets it as a blank file (satisfying the required requirement)
	 */
	public startFormatTypeListener(projectId: string) {
		this.formManager
			.valueChangesFor(ReportFormControl.ExportFormatType)
			.pipe(
				switchMap((exportFormatTypeVal) => {
					if (exportFormatTypeVal === ExportFormatType.P6Excel) {
						return this.cloudStorageService.checkForExistingP6Template$(projectId);
					}
					return of(null as FirebaseStorageMetadata);
				}),
				distinctUntilChanged(),
				map((p6FileMetaData) => {
					if (p6FileMetaData) {
						return new File([], p6FileMetaData.customMetadata?.clientFileName || p6FileMetaData.name);
					} else {
						return null;
					}
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((p6FileMetaData) => {
				if (!p6FileMetaData) {
					this.formManager.patchForm({ [ReportFormControl.ExportFormatTemplate]: null });
				} else {
					this.formManager.patchForm({ [ReportFormControl.ExportFormatTemplate]: p6FileMetaData });
				}
			});
	}

	public createFormGroup(initialReportType: ReportType) {
		const options = {
			reportType: initialReportType,
		} as IReportOptions;

		this.formGroup = this.formManager.create(initialReportType, options);
		return this.formGroup;
	}

	public initFormControlUpdateOnReportTypeChange() {
		this.formManager
			.valueChangesFor(ReportFormControl.ReportType)
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((value) => {
				this.updateFormValueForSelectedReportType(value);
			});

		combineLatest([
			this.featureFlagManager.getFlag$(DynamicFlagTypes.SWIMLANES_REPORT_ENABLED),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.CUSTOM_FIELDS_REPORT_ENABLED),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.EXTERNAL_LINKAGE_REPORT_FILTER_ENABLED),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.WBS_CODE_FILTER_ENABLED),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.PLAN_ARCHIVE_ENABLED),
		])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe({
				next: ([
					isSwimlanesEnabled,
					isCustomFieldsEnabled,
					isExternalLinkageFilterEnabled,
					isWBSCodeFilterEnabled,
					isArchivedPlansEnabled,
				]) => {
					const configOverrides = new Map();

					// If flag is off, set the override on false to disable the control
					if (!isSwimlanesEnabled) {
						configOverrides.set(ReportFormControl.Swimlanes, false);
					}
					if (!isCustomFieldsEnabled) {
						configOverrides.set(ReportFormControl.CustomFields, false);
					}
					if (!isWBSCodeFilterEnabled) {
						configOverrides.set(ReportFormControl.WbsCodeFilterConfig, false);
						configOverrides.set(ReportFormControl.WbsGroupingLevel, false);
					}
					if (!isExternalLinkageFilterEnabled) {
						configOverrides.set(ReportFormControl.ExternalLinkage, false);
					}
					if (!isArchivedPlansEnabled) {
						configOverrides.set(ReportFormControl.ArchivedPlansDropDown, false);
					}

					this.formManager.setOverrideTypeConfig(configOverrides);
				},
				complete: () => this.formManager.setOverrideTypeConfig(new Map(), false),
			});
	}

	/**
	 * Load the last saved report type, or if there is none, return "defaultReportType"
	 */
	public getSavedReportType(defaultReportType: ReportType) {
		return this.userPrefsService.loadReportType() || defaultReportType;
	}

	public initializeReportType(initialReportType: ReportType, defaultReportType: ReportType) {
		let reportType = initialReportType;

		// TP-4884:  If "Export Tickets" was the last report you ran, but since then, the
		// TICKET_EXPORT_ENABLED feature flag has been disabled (or you are not an admin), then we can't load that
		// "Export Tickets" report type:
		if (
			reportType === ReportType.ExportTickets &&
			(!this.featureFlagManager.getFlag(DynamicFlagTypes.TICKET_EXPORT_ENABLED) || !this.isAdmin$.getValue())
		) {
			reportType = defaultReportType;
		}

		this.formManager.patchForm({ reportType } as IReportOptions);

		return reportType;
	}

	public updateFormValueForSelectedReportType(reportType: ReportType) {
		// Load the persisted controls for that type's controls:
		const reportOptions = this.loadPrefsIntoReportOptions(reportType);

		// Update the FormGroup with the loaded values:
		this.formManager.patchForm(reportOptions);
	}

	// returns true when every saved id currently exists, and also returns true when this is not one of the special fields
	// when rejecting a bad id, the entire value for that control is rejected
	private isIdInFirebaseValid(control: ReportFormControl, fbValue: ReportPrefsFirebaseDatatypes) {
		//let the other code handle nulls
		if (fbValue === undefined || fbValue === null) {
			return true;
		}

		switch (control) {
			case ReportFormControl.Plans:
				return (fbValue as string[])?.every((id) => !!this.validIdsCache.plans?.has(id));
			case ReportFormControl.Locations:
				return (fbValue as string[])?.every((id) => !!this.validIdsCache.locations?.has(id));
			case ReportFormControl.Roles:
				return (fbValue as string[])?.every((id) => !!this.validIdsCache.roles?.has(id));
			case ReportFormControl.Swimlanes:
				return (fbValue as string[])?.every((id) => !!this.validIdsCache.swimlanes?.has(id));
			case ReportFormControl.CustomFields:
				return (fbValue as string[])?.every((id) => !!this.validIdsCache.layoutCustomFields?.has(id));
			case ReportFormControl.CustomField1:
			case ReportFormControl.CustomField2:
			case ReportFormControl.CustomField3:
				const filter = fbValue as ICustomFieldFilter;
				return filter && this.validIdsCache.customFields?.has(filter.customFieldId);
			case ReportFormControl.ArchivedPlansDropDown:
				return (fbValue as string[])?.every((id) => !!this.validIdsCache.plans?.has(id));
			default:
				return true;
		}
	}

	/**
	 * Resets the form to default values for the specified report type and saves these defaults
	 * as the initial preferences for future sessions.
	 *
	 * This method clears any saved user preferences for the given report type using the
	 * `clearPreferences` method, making sure that previous user preferences are removed.
	 * Once preferences are cleared, the form is patched with default values for the report type.
	 *
	 * @param reportType - The specific report type for which default settings should be applied and saved.
	 */
	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6556
	// this belongs in the form manager
	public resetAndSaveDefaultsForReportType(reportType: ReportType) {
		this.userPrefsService
			.clearPreferences(reportType)
			.pipe(take(1))
			.subscribe(() => {
				this.formManager.patchForm({ ...DEFAULT_REPORT_FORM_VALUES, reportType } as IReportOptions);
				Object.values(this.formGroup.controls).forEach((control) => {
					control.markAsPristine();
				});
			});
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6556

	private loadPrefsIntoReportOptions(reportType: ReportType) {
		const reportOptions = {} as IReportOptions;
		reportOptions.reportType = reportType;

		const fieldsNotToLoad = [ReportFormControl.ReportType];

		// If the SWIMLANES_REPORT_ENABLED feature flag is disabled, then we can't load the swimlanes control.
		if (!this.featureFlagManager.getFlag(DynamicFlagTypes.SWIMLANES_REPORT_ENABLED)) {
			fieldsNotToLoad.push(ReportFormControl.Swimlanes);
		}

		// If the CUSTOM_FIELDS_REPORT_ENABLED feature flag is disabled, then we can't load the customFields control.
		if (!this.featureFlagManager.getFlag(DynamicFlagTypes.CUSTOM_FIELDS_REPORT_ENABLED)) {
			fieldsNotToLoad.push(ReportFormControl.CustomFields);
		}

		// If the TICKETS_WBS_ENABLED feature flag is disabled, then we can't load the wbs code control.
		if (!this.featureFlagManager.getFlag(DynamicFlagTypes.TICKETS_WBS_ENABLED)) {
			fieldsNotToLoad.push(ReportFormControl.WbsCodeFilterConfig);
		}

		if (!this.featureFlagManager.getFlag(DynamicFlagTypes.WBS_CODE_FILTER_ENABLED)) {
			fieldsNotToLoad.push(ReportFormControl.WbsGroupingLevel);
		}

		if (!this.featureFlagManager.getFlag(DynamicFlagTypes.PLAN_ARCHIVE_ENABLED)) {
			fieldsNotToLoad.push(ReportFormControl.ArchivedPlansDropDown);
		}

		const reportControls = reportsFormTypeConfig[reportType];
		for (const control of reportControls) {
			if (fieldsNotToLoad.includes(control)) {
				continue;
			}
			const firebaseValue = this.userPrefsService.load(reportType, control);
			if (!this.isIdInFirebaseValid(control, firebaseValue)) {
				this.logger.log('control', control, 'in preferences has invalid value', firebaseValue, 'skipping...');
				continue;
			}
			let controlValue;
			if (firebaseValue !== undefined && firebaseValue !== null) {
				controlValue = this.convertFirebaseValueToControlValue(control, firebaseValue);
			} else {
				controlValue = DEFAULT_REPORT_FORM_VALUES[control];
			}
			if (controlValue !== undefined) {
				reportOptions[control as string] = controlValue;
			} else {
				this.logger.error(`Unexpected control type: ${control}, value = ${firebaseValue}`);
			}
		}
		return reportOptions;
	}

	/**
	 * Converts the given form control's value to the format suitable for Firebase storage.
	 * Depending on the control type, this may involve returning the value as-is, handling
	 * specific cases (like setting certain controls to `null`).
	 *
	 * @param control - The specific form control to be processed, identified by `ReportFormControl`.
	 * @param value - The value of the form control that needs to be converted for Firebase storage.
	 * @returns The converted value for Firebase, `null` if special handling is required.
	 */
	private convertControlValueToFirebaseValue(control: ReportFormControl, value: any) {
		// special handling for controls that require specific values
		if (SPECIAL_CASE_CONTROLS_TO_PERSIST.has(control)) {
			return null;
		}

		// default case: unrecognized or unexpected control
		return value;
	}

	/**
	 * Converts a value retrieved from Firebase to the appropriate control value.
	 * This method handles cases where values can be returned directly, special cases
	 * like `ExportFormatTemplate` which returns `null`.
	 *
	 * @param control - The specific form control to be processed, identified by `ReportFormControl`.
	 * @param firebaseValue - The value retrieved from Firebase for the given control.
	 * @returns The converted control value, `null` if special handling is required.
	 */
	private convertFirebaseValueToControlValue(control: ReportFormControl, firebaseValue: any) {
		// special case should always return `null`
		if (SPECIAL_CASE_CONTROLS_TO_READ.has(control)) {
			return null;
		}

		return firebaseValue;
	}

	public saveControlValue(reportType: ReportType, control: ReportFormControl, value: string) {
		if (!this.formGroup.get(control)) {
			return;
		}

		if (control === ReportFormControl.ReportType) {
			this.userPrefsService.storeReportType(reportType);
			return;
		}

		const firebaseValue = this.convertControlValueToFirebaseValue(control, value);
		if (firebaseValue !== undefined) {
			this.userPrefsService.store(reportType, control, firebaseValue);
		} else {
			this.logger.error(`Unexpected control type: ${control as string}, value = ${value}`);
		}
	}

	public anyControlIsBothInvalidAndDirty$() {
		// Feel free to delete "logValidityChanges" and the corresponding "if" statements
		// when we're done dev-testing this stuff...
		const logValidityChanges = false;

		return this.formGroup.statusChanges.pipe(
			map((newStatus) => {
				if (logValidityChanges) {
					this.logger.log('%c OVERALL UI status change:', 'background: #BBFFFF', newStatus);
				}
				if (newStatus === 'VALID') {
					return false;
				}
				if (logValidityChanges) {
					// Look at the particular control(s) that are invalid; log which are dirty:
					for (const [key, ctrl] of Object.entries(this.formGroup.controls)) {
						if (ctrl.status === 'INVALID') {
							this.logger.log(`    ${key}, INVALID, dirty = ${ctrl.dirty}`, ctrl.value);
						}
					}
					this.logger.log(
						`%c    anyControlIsBothInvalidAndDirty$ => ${this.formGroup.dirty}`,
						'background: #BBFFFF'
					);
				}

				return this.formGroup.dirty;
			})
		);
	}

	public get reportType() {
		return this.reportTypeFormControl?.value;
	}

	public get reportTypeFormControl() {
		return this.formGroup.get('reportType');
	}

	public get valueChange$() {
		return this.formManager.valueChanges;
	}

	public get activeControl$() {
		return this.formManager.activeControl$;
	}

	public valueChangeFor$(controlName: ReportFormControl) {
		return this.formManager.valueChangesFor(controlName);
	}

	/** takes in the ui data, returns an array of composite swimlaneIds and a config for "includeNoSwimlane" */
	private unrollSwimlanes(swimlanes: string[] | undefined): {
		includeNoSwimlane: boolean;
		swimlanes: string[] | undefined;
	} {
		const swimlaneMap = this.validIdsCache.swimlanes as Map<string, ICollapsedSwimlaneDropdownOption>;
		const flattenedSwimlanes = swimlanes?.flatMap((sanitizedLabelKey) => {
			const match = swimlaneMap.get(sanitizedLabelKey)?.compositePlanSwimlaneIdAggregate;
			return match ? match : [];
		});
		return {
			includeNoSwimlane: !flattenedSwimlanes || flattenedSwimlanes.length === 0,
			swimlanes: flattenedSwimlanes,
		};
	}

	/**
	 * Helper function to convert forms to fbdb reports config.
	 * Might not be the worst idea to just completely rewrite this from the
	 * ground up. There are 4 different methods of setting data, defaulting data
	 * etc.
	 * @param  formValue  formGroup value
	 * @param  ppcInclusiveReportName  type of report
	 * @param customFieldDefinitions
	 * @param projectId
	 * @param  reportTitle  title of report
	 */
	public convertReportFormToFbdbConfig = (
		formValue: IReportOptions,
		ppcInclusiveReportName: string,
		customFieldDefinitions: Map<string, CustomFieldDefinition>,
		projectId: string,
		reportTitle: string
	): IAnyReportTypes => {
		const internalReportType = formValue.reportType;
		// return customfields in list format and filter out undefine in value
		const customFieldsFilterList = !!formValue[ReportFormControl.FilterByCustomFieldsToggle]
			? [
					formValue[ReportFormControl.CustomField1],
					formValue[ReportFormControl.CustomField2],
					formValue[ReportFormControl.CustomField3],
			  ]
					.filter((val) => !!val || (val && val.value))
					.map((customField) => {
						// also for dropdown type, convert list of value from ids to labels
						const customFieldId = customField.customFieldId;
						const customFieldItem = customFieldDefinitions.get(customFieldId);
						if (customFieldItem?.view.type === CustomFieldType.DropDownList) {
							const customFieldValueIds = customField.value as string[];
							// customField filters are undefined in cases such as anyValue and notSet.
							// We only need the label conversions for isOneOf.
							if (customFieldValueIds) {
								const customFieldListOfValues = {};
								customFieldItem.view.listOfValues.view.data.forEach(
									(val) => (customFieldListOfValues[val.id] = val)
								);
								const convertedCFValues = customFieldValueIds.map((selectedId) => {
									return customFieldListOfValues[selectedId].label;
								});
								return { ...customField, value: convertedCFValues };
							}
						}
						return customField;
					})
			: [];

		const asOfDate = formValue[ReportFormControl.AsOfDate];
		const endDate = formValue[ReportFormControl.EndDate];
		const numberOfWeeks = formValue[ReportFormControl.NumberOfWeeks];
		const upToDate = this.determineUpToDate(internalReportType, asOfDate, endDate, numberOfWeeks);

		/*
			"includeNoLocation" is deprecated at the UI level.

			There used to be a corresponding checkbox in the "old" reports UI, but it was decided that that
			checkbox should not be included in the "new" reports UI.
			However, the Backend's requests did not remove the corresponding "includeNoLocation" param,
			so the Frontend needs to pass ... something.

			The following code determines that "something" value, based on what's selected in the Locations dropdown.
			In effect, if nothing is selected in the Locations dropdown, "null" is passed, and that means the
			Backend's default of "true" takes effect.  Otherwise, if the user selects one or more locations from
			the dropdown, "false" is passed.

			Bottom line:
				filtering by specific locations => exclude "no-loc" tickets
				don't filter by any locations   => include "no-loc" tickets

			P.S. the frontend still passes "something", as opposed to letting the Backend infer this from the
			selected locations, in case we change out minds in the future and reintroduce a "no-loc" checkbox.
		*/
		const includeNoLocation =
			formValue[ReportFormControl.Locations] && formValue[ReportFormControl.Locations]?.length > 0
				? false
				: null;

		// TP-4728: If the report UI doesn't have a "Group By" control, pass null instead of false for groupByLocation:
		const groupByLocLegacy = reportsFormTypeConfig[internalReportType].includes(ReportFormControl.GroupBy)
			? formValue[ReportFormControl.GroupBy] === GroupBy.Location
			: null; // null causes the backend to use its default of true; that's what we want when there's no "Group By" control

		let orderLocationByNameLegacy = undefined;
		if (reportsFormTypeConfig[internalReportType].includes(ReportFormControl.SortGroupsBy)) {
			orderLocationByNameLegacy = formValue[ReportFormControl.SortGroupsBy] === SortGroupsBy.Location;
		}

		// don't really see any discernible reason why groupBy needs special behavior like "groupByLocLegacy" did
		let groupBy = undefined;
		if (reportsFormTypeConfig[internalReportType].includes(ReportFormControl.GroupBy)) {
			groupBy = formValue[ReportFormControl.GroupBy] || GroupBy.Off;
		}

		let orderGroupByName = undefined;
		if (reportsFormTypeConfig[internalReportType].includes(ReportFormControl.SortGroupsBy)) {
			orderGroupByName =
				formValue[ReportFormControl.SortGroupsBy] === SortGroupsBy.Location ||
				formValue[ReportFormControl.SortGroupsBy] === SortGroupsBy.Swimlane ||
				formValue[ReportFormControl.SortGroupsBy] === SortGroupsBy.Wbs;
		}

		// For "Advanced Variance Reason" and “Basic Variance Reason” reports,
		// we always want to order Group By Name, so we set this to true:
		if ([ReportType.AdvancedVarianceReason, ReportType.BasicVarianceReason].includes(internalReportType)) {
			orderGroupByName = true;
		}

		const unrolledSwimlanesObject = this.unrollSwimlanes(formValue[ReportFormControl.Swimlanes]);

		const config: IAnyReportTypes = {
			projectId,
			reportName: ppcInclusiveReportName,
			reportTitle,
			asOfDate,
			upToDate,
			roleIds: formValue[ReportFormControl.Roles],
			planIds: formValue[ReportFormControl.Plans],
			swimlaneIds: unrolledSwimlanesObject.swimlanes,
			locationIds: formValue[ReportFormControl.Locations],
			includeCompleted: formValue[ReportFormControl.IncludeCompleted],
			includeNoLocation,
			includeNoSwimlane: unrolledSwimlanesObject.includeNoSwimlane,
			onlyLateConstraints: formValue[ReportFormControl.OnlyLate],
			granularity: formValue[ReportFormControl.TimeScale],
			customFieldFilters: customFieldsFilterList,
			includePullTickets: formValue[ReportFormControl.IncludePullTickets],
			ticketTypes: formValue[ReportFormControl.TicketTypes],
			externalLinkage: formValue[ReportFormControl.ExternalLinkage],
			exportTicketsFormat: formValue[ReportFormControl.ExportFormatType],
			wbsCodeFilter: formValue[ReportFormControl.WbsCodeFilterConfig],
			wbsGroupingLevel: formValue[ReportFormControl.WbsGroupingLevel],
		};

		if (
			this.featureFlagManager.getFlag(DynamicFlagTypes.SWIMLANES_REPORT_ENABLED) ||
			this.featureFlagManager.getFlag(DynamicFlagTypes.TICKETS_WBS_ENABLED)
		) {
			config['groupBy'] = groupBy;
			config['orderGroupByName'] = orderGroupByName;
		} else {
			config['groupByLocation'] = groupByLocLegacy;
			config['orderLocationByName'] = orderLocationByNameLegacy;
		}
		// We don't include these in the above "config" assignment:
		//     orderByDate: formValue[ReportFormControl.SortBy] === SortBy.Date,
		//
		// because the "forEach" ugly hack below won't take care of keeping true/false out of the config
		// when SortBy or SortGroupsBy isn't included in the form.  If we DID do that, then the Backend will
		// complain and return things like this, for "ticketExport":
		//
		//     Param not recognized: orderByDate
		//
		// Instead, we use these "if" statements.
		//
		if (reportsFormTypeConfig[internalReportType].includes(ReportFormControl.SortBy)) {
			config['orderByDate'] = formValue[ReportFormControl.SortBy] === SortBy.Date;
		}

		if (
			this.featureFlagManager.getFlag(DynamicFlagTypes.CUSTOM_FIELDS_REPORT_ENABLED) &&
			reportsFormTypeConfig[internalReportType].includes(ReportFormControl.CustomFields)
		) {
			config['customFieldIds'] = formValue[ReportFormControl.CustomFields];
		}

		if (
			this.featureFlagManager.getFlag(DynamicFlagTypes.PLAN_ARCHIVE_ENABLED) &&
			formValue[ReportFormControl.ArchivedPlansDropDown]
		) {
			config.planIds = config.planIds.concat(formValue[ReportFormControl.ArchivedPlansDropDown]);
		}

		// Remove fields that are not in that report form type
		Object.keys(config).forEach((key) => {
			if (config[key] === undefined) {
				delete config[key];
			}
		});

		// The "ticketExport" FBDB request type is stringent about param validation, so we need to
		// exclude certain config params:
		if (internalReportType === ReportType.ExportTickets) {
			// The Backend actively complains about being passed an unused "reportName" param,
			// in the export-tickets case:
			delete config['reportName'];

			// Also considered invalid for export-tickets:
			delete config['includeNoLocation'];
			// might as well make includeNoSwimlane behave the same as it's progenitor
			delete config['includeNoSwimlane'];
		}
		return config;
	};

	private determineUpToDate(
		reportType: ReportType,
		asOfDate: string,
		endDate: string,
		numberOfWeeks: number
	) {
		if (reportsFormTypeConfig[reportType].includes(ReportFormControl.NumberOfWeeks)) {
			// The "numberOfWeeks" needs to be converted to days, then added to the "asOfDate" and returned:
			const numberOfDays = 7 * numberOfWeeks;
			return dateCache.addDays(asOfDate, numberOfDays);
		}
		return endDate;
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
